import { FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Upload } from 'lucide-react';

import { useActiveSubject } from '../context/ActiveSubjectContext';
import { useAuth } from '../context/AuthContext';
import { assetService } from '../services/assetService';
import {
  decodeAnswerSheetQrFromFile,
  parseTtqasAnchorFromFileQr,
} from '../utils/decodeAnswerSheetQrFromFile';

export function AnswerSheetScanPage() {
  const { user } = useAuth();
  const { activeSubject } = useActiveSubject();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bindTtqaId = String(searchParams.get('bindTtqaId') || '').trim();
  const answerSheetKind = searchParams.get('answerSheetKind') === 'ttqas' ? 'ttqas' : 'pas';
  const finishMode = String(searchParams.get('finishMode') || '').trim();
  const isTtqasMode = answerSheetKind === 'ttqas';

  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const title = isTtqasMode ? '扫描品题答题纸' : '扫描实战答题纸';
  const returnPath = useMemo(() => {
    if (finishMode === 'ttq-session' && bindTtqaId) {
      return `/ttq/${encodeURIComponent(bindTtqaId)}`;
    }
    return isTtqasMode ? '/ttq' : '/pas';
  }, [bindTtqaId, finishMode, isTtqasMode]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!user?.id || files.length === 0) return;
    setSubmitting(true);
    setError(null);
    setStatus(null);

    let uploaded = 0;
    let skipped = 0;
    try {
      for (const file of files) {
        setStatus(`正在处理 ${uploaded + skipped + 1} / ${files.length}…`);
        const qrRaw = await decodeAnswerSheetQrFromFile(file);
        const ttqasAnchor = isTtqasMode ? parseTtqasAnchorFromFileQr(qrRaw) : null;
        if (isTtqasMode && !ttqasAnchor) {
          throw new Error('未能从图片识别品题答题纸二维码，请换一张更清晰的扫描图。');
        }
        if (isTtqasMode && bindTtqaId && ttqasAnchor && ttqasAnchor.ttqaId !== bindTtqaId) {
          throw new Error('该答题纸不属于当前品题会话，请确认打印来源。');
        }
        try {
          await assetService.uploadImageAsset({
            userId: user.id,
            file,
            subject: activeSubject,
            purpose: 'pas',
            type: isTtqasMode ? 'ttqas' : 'pas',
            ttqasAnchor: ttqasAnchor
              ? {
                  kpId: ttqasAnchor.kpId,
                  ttqaId: ttqasAnchor.ttqaId,
                  sessionId: ttqasAnchor.sessionId,
                  batch: ttqasAnchor.batch,
                  format: ttqasAnchor.format,
                  raw: ttqasAnchor.raw,
                }
              : undefined,
          });
          uploaded += 1;
        } catch (uploadError) {
          const code = (uploadError as Error & { code?: string }).code;
          if (code === 'TTQAS_PRINT_SESSION_DUPLICATE' || code === 'PAS_PRINT_SESSION_DUPLICATE') {
            skipped += 1;
            continue;
          }
          throw uploadError;
        }
      }
      if (uploaded === 0 && skipped > 0) {
        setError('所选图片均已扫描过，未重复上传。');
        return;
      }
      navigate(returnPath, {
        replace: true,
        state: {
          ttqScanNotice: uploaded > 0
            ? `已上传 ${uploaded} 张品题答题纸${skipped > 0 ? `（跳过 ${skipped} 张重复）` : ''}`
            : null,
        },
      });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : '上传失败');
    } finally {
      setSubmitting(false);
      setStatus(null);
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div className="flex items-center gap-2">
        <Link to={returnPath} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
          <ArrowLeft size={16} />
          返回
        </Link>
      </div>
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-ink">{title}</h1>
        <p className="text-sm text-muted">
          Web 版使用图片文件代替相机扫描。请选择已填写的答题纸照片；系统会尝试识别页眉二维码并绑定到对应知识颗粒。
        </p>
        {isTtqasMode && bindTtqaId ? (
          <p className="text-xs text-muted">
            当前绑定品题会话：
            <span className="font-mono text-ink">{bindTtqaId}</span>
          </p>
        ) : null}
      </div>

      <form onSubmit={(event) => { void handleSubmit(event); }} className="space-y-4 rounded-xl border border-border bg-surface p-6 shadow-card">
        <label className="block text-sm">
          <span className="text-muted">答题纸图片</span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => setFiles(Array.from(event.target.files || []))}
            className="mt-1 block w-full text-sm"
          />
        </label>
        {files.length > 0 ? (
          <p className="text-xs text-muted">已选择 {files.length} 张图片</p>
        ) : null}
        {typeof window !== 'undefined' && !window.BarcodeDetector ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            当前浏览器不支持本地二维码识别。请使用 Chrome / Edge 桌面版，或先在 Mobile2 扫描上传。
          </p>
        ) : null}
        {status ? <p className="text-sm text-muted">{status}</p> : null}
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <button
          type="submit"
          disabled={!files.length || submitting}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {submitting ? '上传中…' : '开始上传'}
        </button>
      </form>
    </div>
  );
}
